import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { ArrowRight, Pencil, Plus, Trash2 } from 'lucide-react'

import { api } from '@/api/client'
import { confirm } from '@/stores/confirm'
import type { ItemMapping } from '@/types/models'
import { useToast } from '@/stores/toast'
import { Modal } from '@/components/Modal'
import { GroupPicker } from '@/components/GroupPicker'
import { SearchableGroupPicker } from '@/components/SearchableGroupPicker'
import { QuoteMappingPicker } from '@/components/QuoteMappingPicker'
import { SearchBox } from '@/components/SearchBox'
import { Field, inputCls } from '@/components/Field'
import { cn } from '@/lib/utils'
import { useListNavigation } from '@/lib/useListNavigation'
import { useFormFieldNavigation } from '@/lib/useFormFieldNavigation'

const FORM_FIELDS = ['group', 'quote', 'source_code', 'source_name'] as const

interface PoForm {
  id?: number
  group: number | null
  group_label: string
  // 브릿지 견적 매핑에서 확정되는 값들
  quote_code: string
  quote_name: string
  item: number | null
  item_label: string
  // 발주서 쪽 입력
  source_code: string   // 발주서 코드
  source_name: string   // 발주서 품명
}

const blankForm: PoForm = {
  group: null, group_label: '',
  quote_code: '', quote_name: '',
  item: null, item_label: '',
  source_code: '', source_name: '',
}

export function PoMappingsTab() {
  const qc = useQueryClient()
  const push = useToast((s) => s.push)
  const [groupId, setGroupId] = useState<number | null>(null)
  const [search, setSearch] = useState('')
  const [edit, setEdit] = useState<PoForm | null>(null)

  const { data: rows = [], isFetching } = useQuery({
    queryKey: ['item-mappings', 'po', groupId, search],
    queryFn: () => {
      const params = new URLSearchParams()
      params.set('kind', 'po')   // 발주서 매핑만
      if (groupId) params.set('group_id', String(groupId))
      if (search) params.set('q', search)
      return api<ItemMapping[]>(`/api/item-mappings?${params}`)
    },
  })

  const remove = useMutation({
    mutationFn: (id: number) => api(`/api/item-mappings/${id}/`, { method: 'DELETE' }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['item-mappings', 'po'] })
      push('삭제됨', 'success')
    },
    onError: (e) => push(`삭제 실패: ${e.message}`, 'error', 5000),
  })

  const save = useMutation({
    mutationFn: (f: PoForm) => {
      const body = {
        is_po_bridge: true,                  // 발주서 매핑 (탭 구분 기준)
        source_code: f.source_code.trim(),   // 발주서 코드
        source_name: f.source_name.trim(),   // 발주서 품명
        quote_code: f.quote_code.trim(),     // 브릿지 견적코드 (이름기반이면 빈값)
        item: f.item,
        group: f.group,
        partner: null,
      }
      return f.id
        ? api<ItemMapping>(`/api/item-mappings/${f.id}/`, { method: 'PATCH', body })
        : api<ItemMapping>('/api/item-mappings/', { method: 'POST', body })
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['item-mappings', 'po'] })
      setEdit(null); push('저장됨', 'success')
    },
    onError: (e) => push(`저장 실패: ${e.message}`, 'error', 6000),
  })

  function openAdd() {
    setEdit({ ...blankForm })
  }

  function openEdit(r: ItemMapping) {
    setEdit({
      id: r.id,
      group: r.group, group_label: r.group_name ?? '',
      quote_code: r.quote_code || '',
      quote_name: r.quote_name || '',
      item: r.item, item_label: r.item_name ? `${r.item_code} ${r.item_name}` : '',
      source_code: r.source_code || '',
      source_name: r.source_name || '',
    })
  }

  const { formRef, onKeyDown: onFormKeyDown } = useFormFieldNavigation(FORM_FIELDS)

  const { tbodyRef, onKeyDown: onListKeyDown } = useListNavigation<ItemMapping>({
    items: rows,
    disabled: !!edit,
    onEnter: (r) => openEdit(r),
    onDelete: (r) => {
      void confirm(`발주코드 '${r.source_code}' 매핑 삭제?`, { danger: true }).then((ok) => ok && remove.mutate(r.id))
    },
  })

  // 발주코드 + 그룹 + 연결 품목(견적 피커로 확정) 필수.
  // quote_code(견적코드)는 이름기반 거래처(더위크)에선 빈값일 수 있어 필수 아님.
  const canSubmit = edit
    ? !!edit.group && !!edit.source_code.trim() && !!edit.item
    : false

  return (
    <div className="max-w-7xl mx-auto p-6">
      <div className="sticky top-0 z-20 -mx-6 px-6 py-2 bg-slate-50/95 backdrop-blur border-b border-slate-200/60 mb-3">
      <div className="bg-white rounded-xl border border-slate-200 p-3 flex items-center gap-3 flex-wrap shadow-sm">
        <SearchableGroupPicker
          value={groupId}
          onChange={(g) => setGroupId(g?.id ?? null)}
          placeholder="상위 거래처 필터"
          className="w-64"
        />
        <SearchBox value={search} onChange={setSearch}
                   placeholder="발주코드/견적코드/품명/품목 검색" className="flex-1 min-w-[200px]" />
        <span className="text-xs text-slate-400 tabular-nums">{rows.length}건</span>
        <button onClick={openAdd} data-role="add-btn"
                className="bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-semibold rounded-lg px-3 py-1.5 inline-flex items-center gap-1">
          <Plus className="w-3.5 h-3.5"/> 매핑 추가
        </button>
      </div>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-xs uppercase text-slate-500 tracking-wide">
            <tr>
              <th className="px-3 py-2.5 text-left font-medium w-40">상위 거래처</th>
              <th className="px-3 py-2.5 text-left font-medium w-28">견적서 코드</th>
              <th className="px-3 py-2.5 text-left font-medium">견적서 품명</th>
              <th className="px-3 py-2.5 text-center font-medium w-8"></th>
              <th className="px-3 py-2.5 text-left font-medium w-28">발주서 코드</th>
              <th className="px-3 py-2.5 text-left font-medium">발주서 품명</th>
              <th className="px-3 py-2.5 text-left font-medium w-48">매핑 품목</th>
              <th className="px-3 py-2.5 text-right font-medium w-20"></th>
            </tr>
          </thead>
          <tbody ref={tbodyRef} onKeyDown={onListKeyDown}>
            {rows.map((r, idx) => (
              <tr key={r.id} data-idx={idx} tabIndex={idx === 0 ? 0 : -1}
                  className="border-t border-slate-100 hover:bg-slate-50 focus:bg-blue-50 focus:outline-none">
                <td className="px-3 py-1.5 text-xs font-medium text-slate-800">{r.group_name ?? '—'}</td>
                <td className="px-3 py-1.5 font-mono text-xs tabular-nums text-slate-500">{r.quote_code || '—'}</td>
                <td className="px-3 py-1.5 text-slate-500 text-xs">{r.quote_name || '—'}</td>
                <td className="px-3 py-1.5 text-center text-slate-300"><ArrowRight className="w-3.5 h-3.5 inline"/></td>
                <td className="px-3 py-1.5 font-mono text-xs tabular-nums text-slate-700">{r.source_code || '—'}</td>
                <td className="px-3 py-1.5 text-slate-800">{r.source_name}</td>
                <td className="px-3 py-1.5 text-slate-800">
                  <span className="font-mono text-xs text-slate-400 mr-1.5">{r.item_code}</span>{r.item_name}
                </td>
                <td className="px-3 py-1.5 text-right whitespace-nowrap">
                  <button onClick={() => openEdit(r)} title="편집"
                          className="text-blue-600 hover:text-blue-800 mr-2">
                    <Pencil className="w-3.5 h-3.5"/>
                  </button>
                  <button onClick={() => { void confirm(`발주코드 '${r.source_code}' 매핑 삭제?`, { danger: true }).then((ok) => ok && remove.mutate(r.id)) }}
                          className="text-rose-500 hover:text-rose-700">
                    <Trash2 className="w-3.5 h-3.5"/>
                  </button>
                </td>
              </tr>
            ))}
            {!rows.length && !isFetching && (
              <tr>
                <td colSpan={8} className={cn('text-center py-10 text-sm text-slate-400')}>
                  발주서 매핑이 없습니다. 우측 상단 "매핑 추가"로 시작하세요.
                  <br/>
                  <span className="text-xs">롯데 등 발주서 코드가 견적서 코드와 다른 거래처용입니다.</span>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <Modal open={!!edit} onClose={() => setEdit(null)}
             title={edit?.id ? '발주서 코드 매핑 수정' : '발주서 코드 매핑 추가'} size="xl">
        {edit && (
          <form ref={formRef} onKeyDown={onFormKeyDown}
                onSubmit={(e) => { e.preventDefault(); if (canSubmit) save.mutate(edit) }}
                className="space-y-3">
            <Field label="상위 거래처 *" full>
              <div data-form-field="group">
                <GroupPicker
                  value={edit.group}
                  onChange={(g) => setEdit({
                    ...edit, group: g.id, group_label: `${g.name} · ${g.member_count}개`,
                    // 그룹 바뀌면 견적 브릿지 초기화 (다른 그룹의 견적코드 방지)
                    quote_code: '', quote_name: '', item: null, item_label: '',
                  })}
                  placeholder={edit.group_label || '상위 거래처 선택'}
                />
              </div>
            </Field>

            <Field label="견적서 코드 (품목 연결) *" full>
              <div data-form-field="quote" className="border border-slate-300 rounded-lg">
                <QuoteMappingPicker
                  groupId={edit.group}
                  value={null}
                  onChange={(m) => setEdit({
                    ...edit,
                    // 이름기반 견적행은 코드가 '-'/빈값 → 견적코드는 비워둠(표시용)
                    quote_code: (m.source_code && m.source_code !== '-') ? m.source_code : '',
                    quote_name: m.source_name,
                    item: m.item,
                    item_label: `${m.item_code} ${m.item_name}`,
                  })}
                  placeholder={
                    edit.quote_code
                      ? `${edit.quote_code} ${edit.quote_name} → ${edit.item_label}`
                      : '견적서 코드/품명 검색'
                  }
                />
              </div>
              <div className="text-[11px] text-slate-400 mt-1">
                기존 견적서 매핑에서 골라 연결합니다. 선택하면 견적품명·품목이 자동 확정됩니다.
                {edit.item_label && (
                  <span className="text-emerald-700 font-medium"> · 연결 품목: {edit.item_label}</span>
                )}
              </div>
            </Field>

            <div className="grid grid-cols-2 gap-3">
              <Field label="발주서 코드 *">
                <input data-form-field="source_code" value={edit.source_code}
                       onChange={(e) => setEdit({ ...edit, source_code: e.target.value })}
                       placeholder="발주서(PO)의 품목코드"
                       className={inputCls + ' font-mono tabular-nums'}/>
              </Field>
              <Field label="발주서 품명">
                <input data-form-field="source_name" value={edit.source_name}
                       onChange={(e) => setEdit({ ...edit, source_name: e.target.value })}
                       placeholder="발주서의 품명"
                       className={inputCls}/>
              </Field>
            </div>
            <div className="text-xs text-slate-400 -mt-1">
              발주서 변환은 이 <b>발주서 코드</b>로 품목을 코드매칭합니다.
            </div>

            <div className="flex justify-end gap-2 pt-2">
              <button type="button" onClick={() => setEdit(null)}
                      className="px-4 py-2 text-sm text-slate-600 hover:bg-slate-100 rounded-lg">취소</button>
              <button type="submit"
                      disabled={!canSubmit || save.isPending}
                      className={cn(
                        'px-4 py-2 text-sm font-semibold rounded-lg',
                        (!canSubmit || save.isPending)
                          ? 'bg-slate-200 text-slate-400 cursor-not-allowed'
                          : 'bg-emerald-600 hover:bg-emerald-700 text-white',
                      )}>
                {save.isPending ? '저장 중...' : '저장'}
              </button>
            </div>
          </form>
        )}
      </Modal>
    </div>
  )
}
